import { useEffect, useState } from "react";
import TextButton from "@shared/v2/TextButton";
import Typography from "@shared/v2/Typography";
import Alert from "@shared/v2/Alert";
import { CircleInfoSolidV6 } from "@shared/v2/Icomoon";
import Tooltip from "@shared/v2/Tooltip";
import { Section } from "../../../Reports/components/BusinessDashboard";
import { useCurrentUser, useFeatureFlags } from "../../../reducers/layoutReducer/selectors";
import PhoneSystemCancelModal from "./PhoneSystemCancelModal/PhoneSystemCancelModal";
import dayjs from "@shared/dayjs";
import { caseTransformingAxios } from "@shared/v2/caseTransformingAxios";
import Button from "@shared/v2/Button";
import { AccountProduct } from "../../../Support/Account/AddOnsSupport/types";

interface PhoneSystemUsage {
  startDate: string;
  endDate: string;
  usageInSeconds: number;
}

const usePhoneSystemUsage = () => {
  const [usage, setUsage] = useState<PhoneSystemUsage>({ usageInSeconds: 0, endDate: "", startDate: "" });
  useEffect(() => {
    caseTransformingAxios
      .get("/products/account_phone_system_usage")
      .then(({ data }) => setUsage(data as PhoneSystemUsage))
      .catch(() => {});
  }, []);
  return usage;
};

interface PlanInfoProps {
  accountProduct?: AccountProduct;
  cancelProduct: (
    accountProductId: string | number,
    cancelForm: { status: string; details: { churnCode: string; churnCodeFeedback: string } },
  ) => Promise<void>;
  loadingSettings?: boolean;
  trackingNumbers: Array<unknown>;
  onChangePlan: () => void;
}

const PlanInfo: React.FC<PlanInfoProps> = ({
  accountProduct,
  cancelProduct,
  loadingSettings,
  trackingNumbers,
  onChangePlan,
}) => {
  const {
    account: { isAdmin, isPlace, isLever },
  } = useCurrentUser() as { account: { isAdmin: boolean; isPlace: boolean; isLever: boolean } };
  const featureFlags = useFeatureFlags() as { phoneSystemChangePlan: boolean };
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const usage = usePhoneSystemUsage();

  const allowOverages = dayjs().isBefore(dayjs("2026-04-01"));

  if (isPlace || isLever || !isAdmin) return null;

  return (
    <div className="tw-flex tw-flex-wrap tw-gap-[8px] tw-overflow-auto">
      {/* @ts-expect-error props */}
      <Section className="tw-p-[32px] tw-flex-1 tw-basis-3/12 tw-min-w-[360px]">
        <div className="tw-flex tw-flex-col tw-gap-[16px]">
          <div>
            <div className="tw-flex tw-justify-between tw-items-center tw-gap-[8px]">
              {/* @ts-expect-error props */}
              <Typography variant="subtitle-s">Current Plan</Typography>
              <div className="tw-flex tw-gap-[8px]">
                {featureFlags.phoneSystemChangePlan && (
                  // @ts-expect-error props
                  <Button schema="secondary" onClick={onChangePlan} disabled={loadingSettings}>
                    Change Plan
                  </Button>
                )}
                <TextButton
                  schema="sentence"
                  size="small"
                  className={`!tw-text-12d !tw-text-gray-50 ${accountProduct?.endedAt ? "" : "tw-underline"}`}
                  onClick={() => setIsCancelModalOpen(true)}
                  disabled={accountProduct?.endedAt !== null}
                >
                  {accountProduct?.endedAt
                    ? `Ends ${dayjs(accountProduct.endedAt).format("MM/DD/YYYY")}`
                    : "Cancel Plan"}
                </TextButton>
              </div>
            </div>
            <div className="tw-flex tw-gap-[8px] tw-items-center">
              {/* @ts-expect-error props */}
              <Typography variant="headline2">
                {accountProduct?.product.name?.split(" - ")[1] ?? ""}
              </Typography>{" "}
              <span>—</span> {trackingNumbers.length}/{accountProduct?.product.details.trackingNumberMax || 0}{" "}
              Tracking Numbers
            </div>
          </div>
          <div className="tw-min-h-[1px] tw-h-[1px] tw-bg-gray-10" />
          {!featureFlags.phoneSystemChangePlan && (
            <div className="tw-flex tw-flex-col tw-gap-[8px]">
              {/* @ts-expect-error props */}
              <Typography variant="subtitle-s">Want to change your plan?</Typography>
              {/* @ts-expect-error props */}
              <Typography variant="body-m">Call support at 855-427-4848.</Typography>
            </div>
          )}
        </div>
      </Section>
      {/* @ts-expect-error props */}
      <Section className="tw-p-[32px] tw-flex-1 tw-basis-7/12">
        <div className="tw-flex tw-flex-col tw-gap-[24px]">
          <div className="tw-flex tw-items-end tw-gap-[8px]">
            {/* @ts-expect-error props */}
            <Typography variant="headline2">Team Usage</Typography>
            {usage.startDate && (
              <span className="tw-text-gray-50">
                during {dayjs(usage.startDate).format("MM/DD/YYYY")} -{" "}
                {dayjs(usage.endDate).format("MM/DD/YYYY")}
              </span>
            )}
          </div>
          <div className="tw-flex tw-gap-[32px]">
            <div className="tw-flex-1 tw-min-w-[300px] tw-flex tw-flex-col tw-gap-[16px]">
              <div className="tw-flex tw-gap-[4px]">
                {/* @ts-expect-error props */}
                <Typography variant="subtitle-m" className="!tw-text-gray-50">
                  Minutes Used
                </Typography>
                {allowOverages && (
                  <Tooltip
                    // @ts-expect-error props
                    multiline
                    placement="right"
                    innerClassName="tw-max-w-[270px]"
                    trigger={<CircleInfoSolidV6 />}
                    content={`Your plan comes with ${accountProduct?.product.details.minutesPerMonth} minutes included, but you won’t be charged for overages accrued through 04/01/2026`}
                  />
                )}
              </div>
              <div className="tw-flex tw-flex-col tw-gap-[6px]">
                <div className="tw-relative tw-h-[13px] tw-rounded-[4px] tw-bg-gray-10">
                  <div
                    className="tw-absolute tw-top-0 tw-left-0 tw-bottom-0 tw-rounded-[4px] tw-bg-theme-primary"
                    style={{
                      width: `${Math.min((usage.usageInSeconds / 60 / (accountProduct?.product.details.minutesPerMonth || 1)) * 100, 100)}%`,
                    }}
                  />
                </div>
                <div className="tw-flex tw-justify-between">
                  <div>0</div>
                  <div>
                    {Math.floor(usage.usageInSeconds / 60)}:
                    {Math.floor(usage.usageInSeconds % 60)
                      .toString()
                      .padStart(2, "0")}
                    <span className="tw-text-12d tw-text-gray-50">
                      {" "}
                      / {accountProduct?.product.details.minutesPerMonth}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            {allowOverages && (
              <>
                <div className="tw-h-full tw-w-[1px] tw-bg-gray-10" />
                <Alert
                  containerClass="tw-max-w-[300px] !tw-text-12d"
                  variant="brivity"
                  text={
                    <>
                      🎉 Since you purchased Phone System as an early adopter, you won’t be billed for
                      overages accrued through 04/01/2026.
                    </>
                  }
                />
              </>
            )}
          </div>
        </div>
      </Section>

      <PhoneSystemCancelModal
        accountProduct={accountProduct}
        trackingNumbers={trackingNumbers}
        onCancelPlan={(accountProductId, details) => {
          cancelProduct(accountProductId, details)
            .then(() => {
              setIsCancelModalOpen(false);
            })
            .catch(() => {
              setIsCancelModalOpen(false);
            });
        }}
        onClose={() => setIsCancelModalOpen(false)}
        open={isCancelModalOpen}
      />
    </div>
  );
};

export default PlanInfo;
